import { Autocomplete, Box, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCoinSearch } from '../../../../../hooks/useCoinSearch';
import { CoinWithSymbol } from '../../../../../services/transaction.types';
import { decodeHtml } from '../../../../../utils/decoder';
import { getDisplayCoin } from '../../../../../utils/displayFormatters';

interface CoinAutocompleteProps {
    value: string;
    displayValue: string;
    label: string;
    onCoinChange: (value: CoinWithSymbol | null | string) => void;
}

type CoinOption = CoinWithSymbol | string;

const CoinAutocomplete: React.FC<CoinAutocompleteProps> = ({ value, displayValue, label, onCoinChange }) => {
    const { t } = useTranslation();
    const { coins, loading, searchCoins, hasSearched, resetSearch } = useCoinSearch([]);
    const [localInputValue, setLocalInputValue] = useState(decodeHtml(displayValue || ''));
    const [isBlurring, setIsBlurring] = useState(false);

    useEffect(() => {
        setLocalInputValue(decodeHtml(displayValue || ''));
    }, [displayValue]);

    const handleBlur = () => {
        if (localInputValue && 
            localInputValue.trim() !== '' && 
            !isBlurring && 
            hasSearched && 
            localInputValue.trim().toUpperCase() !== value &&
            localInputValue.trim().toUpperCase() !== displayValue) {
                
            setIsBlurring(true);
            onCoinChange(localInputValue.trim().toUpperCase());
            setTimeout(() => setIsBlurring(false), 200);
        }
    };

    return (
        <Autocomplete
            freeSolo
            disablePortal
            autoHighlight
            openOnFocus
            value={value}
            inputValue={localInputValue}
            onChange={(_, value: CoinOption | null) => {
                if (value === null) {
                    onCoinChange(null);
                } else if (typeof value === 'string') {
                    // When selecting 'Create' option, only pass the search term
                    const searchTerm = value.replace('Create: ', '');
                    onCoinChange(searchTerm);
                } else {
                    onCoinChange(value);
                }
            }}
            onInputChange={(_, value, reason) => {
                setLocalInputValue(value);
                if (reason === 'input') {
                    if (value === '') {
                        resetSearch();
                    } else {
                        searchCoins(value.toUpperCase());
                    }
                } else if (reason === 'clear') {
                    resetSearch();
                }
            }}
            options={coins}
            filterOptions={(options, params) => {
                const filtered = options.filter((option) => {
                    const value = typeof option === 'string' ? option : option.ticker;
                    return value.toLowerCase().includes(params.inputValue.toLowerCase());
                });

                // Check if there's an exact match (case-insensitive)
                const hasExactMatch = filtered.some((option) => {
                    const value = typeof option === 'string' ? option : option.ticker;
                    return value.toLowerCase() === params.inputValue.toLowerCase();
                });

                if (!loading && hasSearched && params.inputValue !== '' && !hasExactMatch) {
                    filtered.push(params.inputValue.toUpperCase());
                }

                return filtered;
            }}
            getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                // The API response has symbol in the symbol field and ticker in the ticker field
                // getDisplayCoin expects (ticker, coin) where ticker is the display symbol and coin is the fallback
                return `${getDisplayCoin(option.symbol, option.ticker)} - ${option.name}`;
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    onBlur={handleBlur}
                    inputProps={{
                        ...params.inputProps,
                        style: { 
                            textTransform: 'uppercase'
                        },
                    }}
                    sx={{
                        '& .MuiInputBase-root': {
                            borderRadius: 1
                        },
                    }}
                    InputProps={{
                        ...params.InputProps,
                        autoComplete: 'new-password',
                    }}
                />
            )}
            renderOption={(props, option) => {
                if (typeof option === 'string') {
                    const { key, ...otherProps } = props;
                    return <li {...otherProps}>{t('transactions.modal.form.createCoin', { coin: option })}</li>;
                }
                const { key, ...otherProps } = props;
                return (
                    <Box component="li" sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }} {...otherProps}>
                        <img
                            loading="lazy"
                            width="20"
                            src={`https://cointracking.info/img/coins/${option.image}`}
                            alt=""
                        />
                        {getDisplayCoin(option.symbol, option.ticker)} - {option.name}
                    </Box>
                );
            }}
            loading={loading}
            loadingText={t('transactions.modal.form.loadingCoins')}
            noOptionsText={t('transactions.modal.form.noCoinsFound')}


            ListboxProps={{
                style: { 
                    maxHeight: 300,
                    overflow: 'auto'
                },
            }}
        />
    );
};

export default CoinAutocomplete;
